/**
 * useAnalytics Hook
 * Provides analytics tracking functionality to React components
 *
 * @module hooks/useAnalytics
 * @description React hook for GA4 tracking with automatic page view tracking on route changes
 *
 * @example
 * ```tsx
 * const { trackFormSubmit, trackCTA } = useAnalytics({ language: 'en' });
 *
 * // Track form submission
 * trackFormSubmit('contact_form');
 *
 * // Track CTA click
 * trackCTA('Get Started', '/contact');
 * ```
 */

import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackCTAClick,
  trackOutboundLink,
  trackDownload,
  trackServiceInterest,
  trackScrollDepth,
  trackPhoneClick,
  trackEmailClick,
  trackSocialClick,
  hasAnalyticsConsent,
  initializeGA,
} from "@/lib/analytics";

export interface UseAnalyticsOptions {
  /** Current language for bilingual tracking */
  language?: "en" | "es";
  /** Override page title (defaults to document.title) */
  pageTitle?: string;
  /** Disable automatic page view tracking */
  disablePageViewTracking?: boolean;
}

export interface UseAnalyticsReturn {
  /** Track a custom event */
  trackEvent: typeof trackEvent;
  /** Track form submission (generate_lead event) */
  trackFormSubmit: (formName: string) => void;
  /** Track CTA button click */
  trackCTA: (ctaText: string, destination: string) => void;
  /** Track outbound link click */
  trackOutboundLink: typeof trackOutboundLink;
  /** Track file download */
  trackDownload: typeof trackDownload;
  /** Track service page interest */
  trackService: (serviceName: string) => void;
  /** Track scroll depth milestone */
  trackScrollDepth: typeof trackScrollDepth;
  /** Track phone number click */
  trackPhoneClick: typeof trackPhoneClick;
  /** Track email link click */
  trackEmailClick: typeof trackEmailClick;
  /** Track social media link click */
  trackSocialClick: typeof trackSocialClick;
  /** Check if user has given analytics consent */
  hasConsent: () => boolean;
}

export const useAnalytics = (
  options: UseAnalyticsOptions = {}
): UseAnalyticsReturn => {
  const location = useLocation();
  const {
    language = "en",
    pageTitle,
    disablePageViewTracking = false,
  } = options;

  // Initialize GA on mount — US-based business, no consent gating for analytics
  useEffect(() => {
    initializeGA();
  }, []);

  // Track page view on route change
  useEffect(() => {
    if (disablePageViewTracking) return;

    // Small delay to ensure document.title is updated by React Helmet or similar
    const timer = setTimeout(() => {
      const title = pageTitle || document.title;
      trackPageView(location.pathname, title, language);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, pageTitle, language, disablePageViewTracking]);

  // Memoized tracking functions with language context
  const trackFormSubmit = useCallback(
    (formName: string) => {
      trackFormSubmission(formName, language);
    },
    [language]
  );

  const trackCTA = useCallback(
    (ctaText: string, destination: string) => {
      trackCTAClick(ctaText, destination, language);
    },
    [language]
  );

  const trackService = useCallback(
    (serviceName: string) => {
      trackServiceInterest(serviceName, language);
    },
    [language]
  );

  return {
    trackEvent,
    trackFormSubmit,
    trackCTA,
    trackOutboundLink,
    trackDownload,
    trackService,
    trackScrollDepth,
    trackPhoneClick,
    trackEmailClick,
    trackSocialClick,
    hasConsent: hasAnalyticsConsent,
  };
};

export default useAnalytics;
